/** API e stato server del modulo notifiche (SPEC-005). */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

export type NotificationChannelType = 'telegram' | 'email' | 'push';

export type TelegramConfig = {
  chatId: string;
  telegramUserId: number;
  username?: string;
};

export type EmailConfig = {
  address: string;
};

export type PushConfig = {
  endpoint: string;
  userAgent?: string;
};

export type NotificationChannel = {
  id: string;
  channelType: NotificationChannelType;
  enabled: boolean;
  config: TelegramConfig | EmailConfig | PushConfig;
  verifiedAt: string | null;
  lastUsedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type LinkCodeResponse = {
  code: string;
  deepLink: string;
  expiresAt: string;
};

export type NotificationChannelKind = 'telegram' | 'email' | 'push';

export type EventDefinition = {
  eventType: string;
  label: string;
  description: string;
  group: 'auto-trading' | 'analisi' | 'scoperta';
};

export type EventPreference = {
  eventType: string;
  channels: Record<NotificationChannelKind, boolean>;
  /** `true` = recapito senza suono. L'email non ha il concetto e lo ignora. */
  silent: boolean;
};

/**
 * Il catalogo degli eventi arriva **dal server** insieme ai valori: ripeterlo
 * qui significherebbe due elenchi che divergono al primo evento aggiunto, e
 * una pagina che mostra righe inesistenti o ne nasconde di reali.
 */
export type PreferencesResponse = { items: EventPreference[]; events: EventDefinition[] };

export const notificationKeys = {
  preferences: ['notifications', 'preferences'] as const,
  channels: ['notifications', 'channels'] as const,
  vapid: ['notifications', 'vapid'] as const,
};

export function useNotificationChannels(options?: { refetchInterval?: number | false }) {
  return useQuery({
    queryKey: notificationKeys.channels,
    queryFn: ({ signal }) => api.get<{ items: NotificationChannel[] }>('/notifications/channels', signal),
    refetchInterval: options?.refetchInterval ?? false,
  });
}

export function useVapidPublicKey() {
  return useQuery({
    queryKey: notificationKeys.vapid,
    queryFn: ({ signal }) => api.get<{ publicKey: string }>('/notifications/push/public-key', signal),
    enabled: 'serviceWorker' in navigator && 'PushManager' in window,
  });
}

export function useUpsertEmail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { address?: string }) =>
      api.post<NotificationChannel>('/notifications/channels/email', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.channels });
    },
  });
}

export function useCreateTelegramLinkCode() {
  return useMutation({
    mutationFn: () => api.post<LinkCodeResponse>('/notifications/channels/telegram/link-code'),
  });
}

export function useSubscribePush() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { endpoint: string; keys: { p256dh: string; auth: string }; userAgent?: string }) =>
      api.post<NotificationChannel>('/notifications/channels/push/subscribe', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.channels });
    },
  });
}

export function useUnsubscribePush() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/notifications/channels/push/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.channels });
    },
  });
}

export function usePatchChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ type, enabled }: { type: NotificationChannelType; enabled: boolean }) =>
      api.patch<NotificationChannel>(`/notifications/channels/${type}`, { enabled }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.channels });
    },
  });
}

export function useDeleteChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (type: Exclude<NotificationChannelType, 'push'>) =>
      api.delete<void>(`/notifications/channels/${type}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.channels });
    },
  });
}

export function useSendTestNotification() {
  return useMutation({
    mutationFn: () => api.post<{ sent: boolean }>('/notifications/test'),
  });
}


export function useNotificationPreferences() {
  return useQuery({
    queryKey: notificationKeys.preferences,
    queryFn: ({ signal }) => api.get<PreferencesResponse>('/notifications/preferences', signal),
  });
}

/**
 * Salva l'intera matrice, non la singola casella.
 *
 * Il client manda lo stato finale che vuole: è idempotente e non lascia stati
 * intermedi. La risposta riporta la matrice riletta, quindi la pagina mostra
 * ciò che è davvero salvato e non ciò che si sperava di salvare.
 */
export function useSavePreferences() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (items: EventPreference[]) =>
      api.put<PreferencesResponse>('/notifications/preferences', { items }),
    onSuccess: (data) => queryClient.setQueryData(notificationKeys.preferences, data),
  });
}
